// src/hooks/useLiveDashboard.js
import { useEffect, useRef } from "react";
import { useApi } from "./useApi";

const DEFAULT_INTERVAL_MS = 15 * 1000; // 15s — cheap change-token poll, not the heavy fetch
// Minimum gap between two refresh fan-outs. A refresh re-runs every subscribed card's full fetch, and
// background syncs (Defender, GRC sweep) can move the token on every poll — which reloaded the whole
// dashboard every 15–30 s. The first change after a quiet spell still refreshes at once; further
// changes inside the window collapse into one trailing refresh at its end, so none is lost.
export const MIN_REFRESH_GAP_MS = 60 * 1000;

// ── Shared, page-wide poller ────────────────────────────────────────────────────
// A dashboard page renders many independent cards, each of which wants to refresh when the org's
// risks/remediations change. Rather than have every card poll /allRisks/change-token on its own
// (N cards → N requests per interval), all consumers share ONE interval and ONE token here. The
// first subscriber starts the poll; the last to unmount stops it. When the token changes, every
// subscriber's refresh is invoked.
const subscribers = new Set(); // Set<() => void>
let intervalId = null;
let lastToken = null; // baseline; null means "not yet established"
let pollIntervalMs = DEFAULT_INTERVAL_MS;
let sharedApiCall = null; // an apiCall instance (useApi is dependency-free, so any instance works)
let inFlight = false; // guard: never let a slow poll overlap the next tick
let cacheBust = 0; // per-request unique suffix so no layer can serve a stale token from cache
let lastFanOutAt = null; // when subscribers were last refreshed (ms), for the MIN_REFRESH_GAP_MS window
let trailingTimeoutId = null; // pending coalesced refresh, if a change arrived inside the window
let fanOutOnVisible = false; // a coalesced refresh came due while the tab was hidden

const fanOut = () => {
  trailingTimeoutId = null;
  lastFanOutAt = Date.now();
  subscribers.forEach((cb) => {
    try { cb(); } catch { /* one card's refresh throwing must not stop the others */ }
  });
};

// Refresh now, or — inside the gap after the last refresh — once at the end of it.
const requestFanOut = () => {
  if (trailingTimeoutId) return; // a coalesced refresh is already scheduled
  const sinceLast = lastFanOutAt === null ? Infinity : Date.now() - lastFanOutAt;
  if (sinceLast >= MIN_REFRESH_GAP_MS) {
    fanOut();
  } else {
    trailingTimeoutId = setTimeout(() => {
      // Don't refresh a tab nobody is looking at; run it when the tab comes back instead.
      if (document.visibilityState !== "visible") {
        trailingTimeoutId = null;
        fanOutOnVisible = true;
        return;
      }
      fanOut();
    }, MIN_REFRESH_GAP_MS - sinceLast);
  }
};

const checkToken = async () => {
  // Skip while backgrounded — no point polling a tab the user isn't looking at.
  if (document.visibilityState !== "visible") return;
  // No org in context (e.g. after an idle logout cleared it) — nothing to poll.
  if (!localStorage.getItem("selectedOrganization")) return;
  if (!sharedApiCall) return;
  // If the previous poll is still running (slow network), don't stack a second one.
  if (inFlight) return;

  inFlight = true;
  try {
    // suppressAuthRedirect=true: a background poll must not trigger an OIDC re-login loop.
    // The `_` param defeats any browser/proxy/CDN caching of this GET (belt-and-suspenders with the
    // backend's no-store headers) so we always observe the current token.
    const res = await sharedApiCall("GET", `/allRisks/change-token?_=${++cacheBust}`, null, false, null, true);
    const token = res?.token;
    if (token == null) return;

    if (lastToken === null) {
      // First reading — establish the baseline without refreshing (cards already loaded on mount).
      lastToken = token;
    } else if (token !== lastToken) {
      lastToken = token;
      requestFanOut();
    }
  } catch {
    // Transparent: swallow transient poll failures and try again next tick.
  } finally {
    inFlight = false;
  }
};

const handleVisibilityChange = () => {
  // On return to the tab, run any refresh that came due while hidden, then check immediately
  // rather than waiting up to a full interval.
  if (document.visibilityState !== "visible") return;
  if (fanOutOnVisible) {
    fanOutOnVisible = false;
    requestFanOut();
  }
  checkToken();
};

const startPolling = () => {
  if (intervalId) return;
  intervalId = setInterval(checkToken, pollIntervalMs);
  document.addEventListener("visibilitychange", handleVisibilityChange);
};

const stopPolling = () => {
  if (intervalId) clearInterval(intervalId);
  intervalId = null;
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  // Reset the baseline so the next mount re-establishes it (and doesn't fire a stale refresh).
  lastToken = null;
  if (trailingTimeoutId) clearTimeout(trailingTimeoutId);
  trailingTimeoutId = null;
  lastFanOutAt = null;
  fanOutOnVisible = false;
};

const subscribe = (cb, apiCall, intervalMs) => {
  sharedApiCall = apiCall; // keep the most recent instance (all are equivalent)
  pollIntervalMs = intervalMs;
  subscribers.add(cb);
  startPolling();
};

const unsubscribe = (cb) => {
  subscribers.delete(cb);
  if (subscribers.size === 0) stopPolling();
};

/**
 * Transparently keeps a dashboard (or a single card) "live" by refreshing it whenever the org's
 * risks/remediations change — no spinners, no UI, the update just happens.
 *
 * All consumers on a page share ONE /allRisks/change-token poll (see the shared poller above), so
 * you can safely call this from as many independent cards as you like without multiplying requests.
 * The poll pauses while the tab is hidden and re-checks immediately on return. Auth errors on the
 * background poll are suppressed so a transient 401 never bounces the user through OIDC.
 *
 * @param {Object}     opts
 * @param {() => void} opts.refresh   Stable callback (wrap in useCallback) that re-fetches this
 *                                    card's data silently. Called only when the token changes, and
 *                                    at most once per MIN_REFRESH_GAP_MS (later changes coalesce).
 * @param {number}    [opts.intervalMs] Poll interval in ms (default 15000).
 * @param {boolean}   [opts.enabled]    When false, this consumer doesn't subscribe (default true).
 */
export const useLiveDashboard = ({ refresh, intervalMs = DEFAULT_INTERVAL_MS, enabled = true }) => {
  const apiCall = useApi();
  // Keep the latest refresh in a ref so re-subscribing isn't needed when the caller passes a new
  // function identity — the subscription stays stable and always calls the current refresh.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!enabled) return undefined;
    const cb = () => refreshRef.current?.();
    subscribe(cb, apiCall, intervalMs);
    return () => unsubscribe(cb);
  }, [enabled, intervalMs, apiCall]);
};

export default useLiveDashboard;
